import { useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Col,
  FormGroup,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Label,
  Row,
} from "reactstrap";
import RightPanelCard from "components/Common/RightPanelCard";
import { useDispatch, useSelector } from "react-redux";
import { customStyle, REGEX } from "components/Common/constants";
import { addSubUser, getSubUserList } from "actions/subUser";

const initialFields = {
  firstName: {
    value: "",
    error: "",
  },
  lastName: {
    value: "",
    error: "",
  },
  email: {
    value: "",
    error: "",
  },
  contact: {
    value: "",
    error: "",
  },
  password: {
    value: "",
    error: "",
  },
  status: {
    value: "ACTIVE",
    error: "",
  },
};

function AddUserModal({
  isModalOpen,
  closeAddModal,
  notify,
  pageNum,
  setPageNum,
}) {
  const dispatch = useDispatch();
  const [userFormData, setUserFormData] = useState(initialFields);
  const [showPassword, setShowPassword] = useState(false);
  const [showLoaderOnClick, setShowLoaderOnClick] = useState(false);
  const [filterState, setFilterState] = useState({
    searchName: "",
    filterParams: { filters: [], searchString: "" },
  });

  const handleInputChange = (fieldName, newValue) => {
    setUserFormData((prev) => ({
      ...prev,
      [fieldName]: {
        value: newValue,
        error: "",
      },
    }));
  };

  // based on the field name will retuen error if condition meets
  const getValidationError = (name, value) => {
    switch (name) {
      case "firstName":
        return !value
          ? "Please enter a first name"
          : value.length < 2
            ? "First name must be at least 2 characters"
            : value.length > 50
              ? "First name must be less than 50 characters"
              : "";

      case "lastName":
        return !value
          ? "Please enter a last name"
          : value.length < 2
            ? "Last name must be at least 2 characters"
            : value.length > 50
              ? "Last name must be less than 50 characters"
              : "";

      case "email":
        return !value
          ? "Please enter an email"
          : !REGEX.EMAIL_REGEX.test(value)
            ? "Please enter a valid email"
            : "";

      case "contact":
        return !value
          ? "Please enter contact number"
          : !REGEX.CONTACT_REGEX.test(value)
            ? "Please enter a valid 9-digit contact number"
            : "";

      case "password":
        return !value
          ? "Please enter a password"
          : value.length < 8
            ? "The password must contain 8 characters"
            : "";

      default:
        return "";
    }
  };

  //to validate form for all the keys in the userForm object
  const validateForm = () => {
    let newSubUser = { ...userFormData };

    Object.keys(newSubUser).forEach((key) => {
      newSubUser[key] = {
        ...newSubUser[key],
        error: getValidationError(key, newSubUser[key].value),
      };
    });
    setUserFormData(newSubUser);
    return Object.values(newSubUser).every((item) => item.error === "");
  };

  const handleSaveClick = async () => {
    if (validateForm()) {
      try {
        setShowLoaderOnClick(true);
        // bodyData which will se sent to api
        const bodyData = {
          firstName: userFormData.firstName.value,
          lastName: userFormData.lastName.value,
          email: userFormData.email.value,
          phone: userFormData.contact.value,
          password: userFormData.password.value,
          roleID: 5,
        };
        const res = await addSubUser(bodyData);
        if (res.code === 200) {
          notify("success", res.message || "User Added successful");
          closeAddModal();
          //list call on successfull add
          if (pageNum === 1) {
            getSubUserList(dispatch, pageNum, filterState.filterParams);
          } else {
            setPageNum(1);
          }
        } else {
          const message = Array.isArray(res?.message)
            ? res?.message[0]?.Msg
            : res?.message || "Failed to Add User!";
          notify("danger", message);
        }
      } catch (e) {
        notify("danger", "Failed to Add User!");
      } finally {
        setShowLoaderOnClick(false);
      }
    }
  };

  //on blur to get if any validation error exits
  const handleOnBlur = (name, value) => {
    const trimmedValue = typeof value === "string" ? value.trim() : value;
    const error = getValidationError(name, trimmedValue);
    setUserFormData((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        value: trimmedValue,
        error,
      },
    }));
  };

  return (
    <RightPanelCard
      isOpen={isModalOpen}
      onClose={closeAddModal}
    // width={"50vw"}
    >
      <Card className="mb-0">
        <CardHeader
          className="d-flex align-items-center justify-content-between mb-2"
          tag="h4"
        >
          Add User
          <i
            className="fa fa-close text-muted pointer"
            onClick={closeAddModal}
          />
        </CardHeader>
        <CardBody>
          <Row>
            <Col md="6">
              <FormGroup
                className={`has-label ${userFormData.firstName.error ? "has-danger" : ""
                  } d-flex flex-column align-items-start`}
              >
                <Label>
                  First Name <span className="validation-error">*</span>
                </Label>
                <Input
                  name="firstName"
                  type="text"
                  placeholder="Enter first name"
                  autoComplete="off"
                  value={userFormData.firstName.value}
                  onChange={(e) =>
                    handleInputChange(e.target.name, e.target.value)
                  }
                  onBlur={(e) => handleOnBlur(e.target.name, e.target.value)}
                  required
                  maxLength={50}
                />
                {userFormData.firstName.error && (
                  <small className="validation-error">
                    {userFormData.firstName.error}
                  </small>
                )}
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup
                className={`has-label ${userFormData.lastName.error ? "has-danger" : ""
                  } d-flex flex-column align-items-start`}
              >
                <Label>
                  Last Name <span className="validation-error">*</span>
                </Label>
                <Input
                  name="lastName"
                  type="text"
                  placeholder="Enter last name"
                  autoComplete="off"
                  value={userFormData.lastName.value}
                  onChange={(e) =>
                    handleInputChange(e.target.name, e.target.value)
                  }
                  onBlur={(e) => handleOnBlur(e.target.name, e.target.value)}
                  required
                  maxLength={50}
                />
                {userFormData.lastName.error && (
                  <small className="validation-error">
                    {userFormData.lastName.error}
                  </small>
                )}
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md="6">
              <FormGroup
                className={`has-label ${userFormData.email.error ? "has-danger" : ""
                  } d-flex flex-column align-items-start`}
              >
                <Label for="email">
                  Email Address <span className="validation-error">*</span>
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="Enter email address"
                  autoComplete="off"
                  value={userFormData.email.value}
                  onChange={(e) =>
                    handleInputChange(e.target.name, e.target.value)
                  }
                  onBlur={(e) => handleOnBlur(e.target.name, e.target.value)}
                  required
                  maxLength={100}
                />
                {userFormData.email.error && (
                  <small className="validation-error">
                    {userFormData.email.error}
                  </small>
                )}
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup
                className={`has-label ${userFormData.contact.error ? "has-danger" : ""
                  } d-flex flex-column align-items-start`}
              >
                <Label>
                  Contact Number  <span className="validation-error">*</span>
                </Label>

                <InputGroup className="input-group">
                  <InputGroupAddon addonType="prepend">
                    <InputGroupText className="d-flex align-items-center">
                      <span className="mr-1">🇸🇦</span> +966
                    </InputGroupText>
                  </InputGroupAddon>
                  <Input
                    name="contact"
                    type="text"
                    placeholder="Enter contact number"
                    autoComplete="off"
                    value={userFormData.contact.value}
                    onChange={(e) =>
                      handleInputChange(e.target.name, e.target.value)
                    }
                    onBlur={(e) =>
                      handleOnBlur(e.target.name, e.target.value)
                    }
                    required
                    maxLength={9}
                  />
                </InputGroup>
                {userFormData.contact.error && (
                  <small className="validation-error">
                    {userFormData.contact.error}
                  </small>
                )}
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup
                className={`has-label ${userFormData.password.error ? "has-danger" : ""
                  } d-flex flex-column align-items-start`}
              >
                <Label for="password">
                  Password <span className="validation-error">*</span>
                </Label>
                <InputGroup className="input-group">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="off"
                    placeholder="Enter password"
                    value={userFormData.password.value}
                    onChange={(e) =>
                      handleInputChange(e.target.name, e.target.value)
                    }
                    onBlur={(e) => handleOnBlur(e.target.name, e.target.value)}
                    required
                    maxLength={16}
                  />

                  <InputGroupAddon addonType="append">
                    <InputGroupText
                      className="d-flex align-items-center pointer"
                      onClick={() => setShowPassword((prev) => !prev)}
                    >
                      {showPassword ? (
                        <i className="fa fa-eye-slash" />
                      ) : (
                        <i className="fa fa-eye" />
                      )}
                    </InputGroupText>
                  </InputGroupAddon>
                </InputGroup>
                {userFormData.password.error && (
                  <small className="validation-error">
                    {userFormData.password.error}
                  </small>
                )}
              </FormGroup>
            </Col>
          </Row>
        </CardBody>
        <CardFooter className="d-flex justify-content-end">
          <Button color="secondary" onClick={closeAddModal} className="mr-2">
            Cancel
          </Button>
          <Button
            color="default"
            type="submit"
            onClick={() => {
              handleSaveClick();
            }}
            disabled={Object.values(userFormData).some(
              (item) => item.error !== "",
            )}
          >
            {showLoaderOnClick ? (
              <i className="fa fa-spinner fa-spin fa-lg text-white" />
            ) : (
              "Save"
            )}
          </Button>
        </CardFooter>
      </Card>
    </RightPanelCard>
  );
}

export default AddUserModal;
